import React, { useState, useRef, useCallback, useEffect } from 'react';
import GroupChatInput, { type GroupChatInputMessage } from '@/components/GroupChatInput';
import {
  createComment,
  getCommentList,
  likeComment,
  deleteComment,
  type CommentRecord,
} from '@/service/comment';
import { uploadImage } from '@/service/chat';
import { message } from 'starry-sky-ui';
import './index.scss';

interface CommentSectionProps {
  /** 评论类型，如 "article" */
  type: string;
  /** 目标 ID（文章/面试题 ID） */
  targetId: number;
}

const STORAGE_KEY = 'comment_user';

interface CommentUser {
  name: string;
  avatar: string;
}

/** 检查是否有 token（管理员） */
const isAdmin = (): boolean => !!localStorage.getItem('token');

/** 格式化时间 */
function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60000) return '刚刚';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} 分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小时前`;
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${month}-${day}`;
}

/** 将 segments 转为带图片引用的纯文本（图片转 markdown 语法） */
function segmentsToContent(segments: GroupChatInputMessage['segments']): string {
  return segments
    .map((s) => {
      if (s.type === 'image' && s.imageUrl) {
        return `![image](${s.imageUrl})`;
      }
      return s.content?.trim() || '';
    })
    .filter(Boolean)
    .join(' ');
}

/** 渲染评论内容，支持 markdown 图片语法 */
function renderContent(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /!\[image\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <img key={match.index} src={match[1]} alt="评论图片" className="comment-item__inline-img" loading="lazy" />,
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

const CommentSection: React.FC<CommentSectionProps> = ({ type, targetId }) => {
  const [user, setUser] = useState<CommentUser | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // ---- 用户设置弹窗 ----
  const [showUserModal, setShowUserModal] = useState(!user);
  const [newName, setNewName] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [nameError, setNameError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- 评论数据 ----
  const [comments, setComments] = useState<CommentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCommentList({ type, targetId });
      const list = res?.data?.list;
      if (res?.success && Array.isArray(list)) {
        setComments(list);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [type, targetId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // ---- 头像上传 ----
  const handleAvatarSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setNameError('请选择图片文件'); return; }
    if (file.size > 2 * 1024 * 1024) { setNameError('头像大小不能超过 2MB'); return; }

    setAvatarPreview(URL.createObjectURL(file));
    setAvatarUploading(true);
    setNameError('');

    try {
      const { url } = await uploadImage(file);
      setAvatarUrl(url);
    } catch (err: any) {
      setNameError(err.message || '头像上传失败');
      setAvatarPreview(null);
    } finally {
      setAvatarUploading(false);
    }
  }, []);

  // ---- 确认用户信息（一次性，不可修改） ----
  const handleUserConfirm = useCallback(() => {
    const trimmed = newName.trim();
    if (!trimmed) { setNameError('请输入昵称'); return; }
    if (trimmed.length > 20) { setNameError('昵称不能超过 20 个字符'); return; }
    if (!avatarUrl) { setNameError('请上传头像'); return; }

    const userData: CommentUser = { name: trimmed, avatar: avatarUrl };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
    setUser(userData);
    setShowUserModal(false);
  }, [newName, avatarUrl]);

  // ---- 发送评论 ----
  const handleSend = useCallback(
    async (msg: GroupChatInputMessage) => {
      const content = segmentsToContent(msg.segments);
      if (!content || !user || sending) return;

      setSending(true);
      try {
        const res = await createComment({
          type,
          targetId,
          name: user.name,
          content,
          avatar: user.avatar,
        });
        if (res?.success) {
          message.success('评论成功！');
          fetchComments();
        } else {
          message.error(res?.message || '评论失败');
        }
      } catch {
        message.error('网络错误');
      } finally {
        setSending(false);
      }
    },
    [user, sending, type, targetId, fetchComments],
  );

  // ---- 点赞（不限次数，点赞后刷新列表） ----
  const handleLike = useCallback(async (id: number) => {
    try {
      const res = await likeComment(id);
      if (res?.success) {
        fetchComments();
      }
    } catch {
      // ignore
    }
  }, [fetchComments]);

  // ---- 删除 ----
  const handleDelete = useCallback(async (id: number) => {
    try {
      const res = await deleteComment(id);
      if (res?.success) {
        message.success('评论已删除');
        setComments((prev) => prev.filter((c) => c.id !== id));
      }
    } catch {
      // ignore
    }
  }, []);

  // ---- 输入框 key：user 变化时重建，确保发送后内容清空 ----
  const [inputKey, setInputKey] = useState(0);
  useEffect(() => {
    if (user) setInputKey((k) => k + 1);
  }, [user]);

  return (
    <>
      {/* ---- 用户设置弹窗 ---- */}
      {showUserModal && (
        <div className="comment-user-overlay">
          <div className="comment-user-modal">
            <h2 className="comment-user-modal__title">设置用户信息</h2>
            <p className="comment-user-modal__desc">设置昵称和头像后即可评论，设置后不可修改</p>
            <div className="comment-user-modal__avatar-section">
              <div
                className="comment-user-modal__avatar"
                onClick={() => !avatarUploading && fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
              >
                {avatarUploading ? (
                  <span className="comment-user-modal__avatar-placeholder">
                    <span className="comment-user-modal__spinner" />
                  </span>
                ) : avatarPreview ? (
                  <img src={avatarPreview} alt="头像预览" />
                ) : (
                  <span className="comment-user-modal__avatar-placeholder">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </span>
                )}
                <span className="comment-user-modal__avatar-hint">
                  {avatarUploading ? '上传中...' : '点击上传头像'}
                </span>
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarSelect} style={{ display: 'none' }} />
            </div>
            <div className="comment-user-modal__input-group">
              <label className="comment-user-modal__label">昵称</label>
              <input
                className="comment-user-modal__input"
                type="text"
                value={newName}
                onChange={(e) => { setNewName(e.target.value); setNameError(''); }}
                placeholder="输入你的昵称"
                maxLength={20}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleUserConfirm()}
              />
            </div>
            {nameError && <p className="comment-user-modal__error">{nameError}</p>}
            <button
              className="comment-user-modal__confirm"
              onClick={handleUserConfirm}
              disabled={!newName.trim() || !avatarUrl || avatarUploading}
            >
              {avatarUploading ? '头像上传中...' : '开始评论'}
            </button>
          </div>
        </div>
      )}

      {/* ---- 评论区 ---- */}
      <div className="comment-section" id="comment-section">
        <h3 className="comment-section__title">
          评论
          <span className="comment-section__count">({comments.length})</span>
        </h3>

        {/* 输入框 — 直接使用 GroupChatInput */}
        {user && (
          <div className="comment-input-area">
            <div className="comment-input-area__header">
              {user.avatar ? (
                <img src={user.avatar} alt="" className="comment-input-area__avatar" />
              ) : (
                <span className="comment-input-area__avatar comment-input-area__avatar--text">
                  {user.name.charAt(0).toUpperCase()}
                </span>
              )}
              <span className="comment-input-area__name">{user.name}</span>
            </div>
            <GroupChatInput
              key={inputKey}
              onSend={handleSend}
              sending={sending}
              placeholder="写下你的评论..."
            />
          </div>
        )}

        {!user && (
          <div className="comment-input-trigger">
            <span className="comment-input-trigger__text">设置昵称和头像，开始评论</span>
            <button className="comment-input-trigger__btn" onClick={() => setShowUserModal(true)}>
              去设置
            </button>
          </div>
        )}

        {/* 列表 */}
        {loading ? (
          <div className="comment-loading">加载中...</div>
        ) : comments.length === 0 ? (
          <div className="comment-empty">暂无评论，快来抢沙发吧～</div>
        ) : (
          <div className="comment-list">
            {comments.map((comment, index) => (
              <div key={comment.id} className="comment-item">
                <div className="comment-item__header">
                  {comment.avatar ? (
                    <img src={comment.avatar} alt="" className="comment-item__avatar" />
                  ) : (
                    <span className="comment-item__avatar comment-item__avatar--text">
                      {comment.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="comment-item__info">
                    <span className="comment-item__name">{comment.name}</span>
                    <span className="comment-item__floor">{comments.length - index}楼</span>
                    <span className="comment-item__time">{formatTime(comment.createdAt)}</span>
                  </div>
                </div>
                <p className="comment-item__content">{renderContent(comment.content)}</p>
                <div className="comment-item__actions">
                  <button
                    className="comment-item__action"
                    onClick={() => handleLike(comment.id)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                    </svg>
                    {comment.likes > 0 ? comment.likes : '赞'}
                  </button>
                  {user && (user.name === comment.name || isAdmin()) && (
                    <button
                      className="comment-item__action"
                      onClick={() => handleDelete(comment.id)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                      删除
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default CommentSection;
